import { getImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import gsap from "gsap";
import React, { useEffect, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";

import useOnScreen from "./use-on-screen";

const GridLayout3 = ({ items }) => {
	const containerRef = useRef(null);
	const isVisible = useOnScreen(containerRef, "10px");

	useEffect(() => {
		if (isVisible) {
			const tl = gsap.timeline({ defaults: { ease: "power2.out" } });

			// A) Left column all at once from the left
			tl.to(".left-card", {
				x: 0,
				opacity: 1,
				duration: 0.8,
				stagger: 0, // move together
				delay: 1,
			});

			// B) Top row of the right column (indexes 0 & 1) up from bottom
			tl.to(
				".right-card.top-row",
				{
					y: 0,
					opacity: 1,
					duration: 0.8,
					stagger: 0, // move together
				},
				"-=0.5"
			);

			// C) Bottom row of the right column (index 2) up last
			tl.to(
				".right-card.bottom-row",
				{
					y: 0,
					opacity: 1,
					duration: 0.8,
				},
				"-=0.6"
			);
		}
	}, [isVisible]);

	return (
		<>
			{/* Inline CSS to ensure initial hidden states. 
         Note the classes now go on the wrapper .animate-item */}
			<style>
				{`
          .animate-item {
            opacity: 0;
          }
          .left-card {
            transform: translateX(-50px);
          }
          .right-card {
            transform: translateY(50px);
          }
        `}
			</style>

			<section className="py-5 py-lg-7" ref={containerRef}>
				<Container>
					<Row className="g-md-4 g-3">
						{/* -----------------------------
                LEFT Column (items 0..2)
               ----------------------------- */}
						<Col lg={6}>
							<Row className="g-md-4 g-3">
								{items.slice(0, 3).map((item, index) => {
									const image = getImage(item.image.localFile);
									const isLarge = index === 0; // first item is large
									const colSize = isLarge ? 12 : 6;

									return (
										<Col xs={colSize} key={index}>
											{/* 
                        1) Our wrapper with .animate-item and .left-card
                        2) BgImage inside that wrapper
                      */}
											<div
												className={`animate-item left-card`}
												style={{ position: "relative" }}
											>
												<BgImage
													image={image}
													className="d-flex align-items-end"
													style={{
														...cardStyles,
														height: isLarge ? "400px" : "200px",
													}}
												>
													<div
														className="w-100 h-100"
														style={overlayStyles}
													></div>
													<div
														style={{ zIndex: 2 }}
														className="position-relative p-md-4 p-2"
													>
														<h3
															className={`text-white ${
																isLarge ? "fs-3" : "fs-5"
															}`}
														>
															{item.heading}
														</h3>
													</div>
												</BgImage>
											</div>
										</Col>
									);
								})}
							</Row>
						</Col>

						{/* ------------------------------
                RIGHT Column (items 3..5)
               ------------------------------ */}
						<Col lg={6}>
							<Row className="g-md-4 g-3">
								{items.slice(3, 6).map((item, index) => {
									const image = getImage(item.image.localFile);
									const isLarge = index === 2; // last item in this group is large
									const colSize = isLarge ? 12 : 6;

									// If index < 2 => top-row, else bottom-row
									const rowClass = index < 2 ? "top-row" : "bottom-row";

									return (
										<Col xs={colSize} key={index}>
											{/* 
                        1) Our wrapper with .animate-item and .right-card
                        2) Also add top-row or bottom-row
                      */}
											<div
												className={`animate-item right-card ${rowClass}`}
												style={{ position: "relative" }}
											>
												<BgImage
													image={image}
													className="d-flex align-items-end"
													style={{
														...cardStyles,
														height: isLarge ? "400px" : "200px",
													}}
												>
													<div
														className="w-100 h-100"
														style={overlayStyles}
													></div>
													<div
														style={{ zIndex: 2 }}
														className="position-relative p-md-4 p-2"
													>
														<h3
															className={`text-white ${
																isLarge ? "fs-3" : "fs-5"
															}`}
														>
															{item.heading}
														</h3>
													</div>
												</BgImage>
											</div>
										</Col>
									);
								})}
							</Row>
						</Col>
					</Row>
				</Container>
			</section>
		</>
	);
};

const cardStyles = {
	position: "relative",
	borderRadius: "12px",
	overflow: "hidden",
};

const overlayStyles = {
	position: "absolute",
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	background:
		"linear-gradient(180deg, rgba(0, 18, 33, 0.00) 6.66%, #001221 95.38%)",
};

export default GridLayout3;
